import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { getDataQuery } from 'api';
import { postDataQuery } from 'api/board/postQuery';
import { postDataBodyQuery } from 'api/getDataBodyQuery';
import { patchDataQuery } from 'api/patchQuery';
import { CardHeader } from 'components/common';
import { AttendState } from 'components/common/AttendState';
import OnlineAttendancePopup from 'components/popup/OnlineAttendancePopup';
import QrCreatePopup from 'components/popup/QrCreatePopup';
import BreadCrumb from 'components/shared/BreadCrumb';
import { Sidebar } from 'components/shared/sidebar';
import { useGetCommonApi } from 'hooks';
import { useGetRequiredApi } from 'hooks/useGetRequiredApi';
import AttendanceProgress from 'pages/AttendanceProgress';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Tab, Table, Tabs } from 'react-bootstrap';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { loginUser } from 'recoil/loginUser';
import { adminSubjectState } from 'recoil/subject/adminSubjectState';
import { subjectOptionDataState } from 'recoil/subject/subjectOptionDataState';
import * as XLSX from 'xlsx-js-style';
import { applyExcelStyles, applyCenterAlignment, writeExcelFile } from 'util/excelStyle';
import { teacherInfoState } from '../../recoil/subject/teacherInfoState';
import { sisCourseState } from '../../recoil/subject/sisCourseState';
import { downloadLoadingState } from 'recoil/loading/downloadLoadingState';
import moment from 'moment';

export const OnlineAttendance = () => {
  const [key, setKey] = useState('onlineAttendance');
  const [visiblePopup, setVisiblePopup] = useState(false);
  const [visibleQrCreatePopup, setVisibleQrCreatePopup] = useState(false);
  const [visibleQrPopup, setVisibleQrPopup] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({ userNumber: '', userLmsNumber: '', userName: '', attendanceCnt: 0, absenceCnt: 0 });
  const [qrCreate, setQrCreate] = useState({courseId: ''});
  const [qrImg, setQrImg] = useState({qrImg: ""});
  const user = useRecoilValue(loginUser);
  const [searchTerm, setSearchTerm] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState(null);
  const [subjectState, setSubjectState] = useRecoilState(subjectOptionDataState);
  const [adminSelectState, setAdminSelectState] = useRecoilState(adminSubjectState);
  const { semestersData } = useGetCommonApi();
  const [selectTeacherInfo, setSelectTeacherInfo] = useRecoilState(teacherInfoState);
  const [sisCourseIdState, setSisCourseIdState] = useRecoilState(sisCourseState);
  const [attendanceListMode, setAttendanceListMode] = useState(false);
  const [filteredAdvisorList, setFilteredAdvisorList] = useState(selectTeacherInfo.filteredAdvisors || []);
  const [selectedMainTeacherNumber, setSelectedMainTeacherNumber] = useState(selectTeacherInfo.managerNumber || '');
  const [isAdvisorLoading, setIsAdvisorLoading] = useState(false);

  const { adminSelectBoxQueryData, adminSubSelectBoxQueryData, adminTeacherInfoSelectBoxQueryData, teacherInfoSelectBoxQueryData, teacherTermSelectBoxQueryData } = useGetRequiredApi(
    adminSelectState.lectureId,
    subjectState.openYy,
    subjectState.openTmGbn,
    sisCourseIdState.scCd,
    sisCourseIdState.openDeptCd
  );

  const teacherInfoData = user.role === 'ADMIN' ? adminTeacherInfoSelectBoxQueryData : teacherInfoSelectBoxQueryData;
  const mainTeachers = useMemo(() => teacherInfoData?.flatMap(item => item.mainTeachers) || [], [teacherInfoData]);
  const advisorTeachers = useMemo(() => teacherInfoData?.flatMap(item => item.advisorTeachers) || [], [teacherInfoData]);

  const myAdvisorCourses = advisorTeachers.filter(item => item.teacherNumber === user.loginId);
  const isAdvisorProfessor = myAdvisorCourses.length > 0;
  const isMainProfessor = mainTeachers.some(item => item.teacherNumber === user.loginId);
  const filteredMainProfessors = (!isAdvisorProfessor && isMainProfessor)
    ? mainTeachers
    : mainTeachers.filter(mainProf =>
        mainProf.teacherNumber === user.loginId ||
        myAdvisorCourses.some(advisorCourse =>
          mainProf.courseTeacherInfo.some(mainCourse =>
            advisorCourse.courseTeacherInfo.some(ac => ac.sisCourseId === mainCourse.sisCourseId)
          )
        )
      );
  const [onlineAttendanceSetting, setOnlineAttendanceSetting] = useState({
    useFlag: true, // 기본값
    acceptanceCriteria: 'OVER_ACCEPTANCE_PROGRESS_RATE',
    moduleAcceptanceCriteria: 'FINISH_ALL_CONTENT_VIDEO', // 기본값
    acceptanceProgressRate: 0, // 기본값
    totalPoint: 30, // 기본값
    minPoint: 0, // 기본값
    latePenaltyPoint: -1,
    absencePenaltyPoint: -2, // 기본값
  });
  const teacherSubjectApis = useQueries({
    queries: [
      {
        // 교수자 - [학습현황] 과목을 수강중인 학생들의 목록
        queryKey: ['teacherSubjectStudentList', user, subjectState],
        queryFn: () => getDataQuery(`/teach/courses/${subjectState.courseId}/students`),
        enabled: !!subjectState.courseId,
      },
      {
        // 교수자 - [출결현황] 출결 데이터
        queryKey: ['teacherOnlineAttendanceList', user, subjectState, attendanceListMode, selectTeacherInfo],
        queryFn: () => getDataQuery(`/attendance/course/${subjectState.courseId}/online?teacherNum=${selectTeacherInfo.advisorNumber || 0}&openYy=${sisCourseIdState.openYy}&openTmGbn=${sisCourseIdState.openTmGbn}&allFlag=${attendanceListMode}`),
        enabled: !!subjectState.courseId && !!sisCourseIdState.openTmGbn && !!sisCourseIdState.openYy,
      },
      {
        // 교수자 - [출결현황] 출석평가표
        queryKey: ['teacherOnlineAttendanceSettingList', user, subjectState],
        queryFn: () => getDataQuery(`/attendance-online-setting/${subjectState.courseId}`),
        enabled: !!subjectState.courseId,
        keepPreviousData: true,
      },
    ],
  });

  const [teacherSubjectStudentListData, teacherAttendanceList, teacherOnlineAttendanceSettingList] =
    teacherSubjectApis?.map((query) => query?.data?.data);

  const [refetchSubjectStudentListData, refetchAttendanceList, refetchOnlineAttendanceSettingList] =
    teacherSubjectApis?.map((query) => query?.refetch) || [];

  const isAttendanceLoading = teacherSubjectApis?.[1]?.isFetching;

  const [sortConfig, setSortConfig] = useState({
    type: 'info',
    key: 'number',
    direction: 'asc',
    moduleNumber: null,
    classIndex: null,
  });

  const sortDir = (dir) => (dir === 'asc' ? 'desc' : 'asc');

  const handleSortInfo = (key) => {
    setSortConfig((prev) => {
      if (prev.type === 'info' && prev.key === key) {
        return {
          ...prev,
          direction: sortDir(prev.direction),
        };
      }
      return { type: 'info', key, direction: 'asc', moduleNumber: null, classIndex: null };
    });
  };

  const handleSortClass = (moduleNumber, classIndex) => {
    setSortConfig((prev) => {
      if (prev.type === 'class' && prev.moduleNumber === moduleNumber && prev.classIndex === classIndex) {
        return { ...prev, direction: sortDir(prev.direction) };
      }
      return { type: 'class', key: null, direction: 'asc', moduleNumber, classIndex };
    });
  };

  const sortArrowInfo = (key) =>
    sortConfig.type === 'info' && sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ' ↕';

  const sortArrowClass = (moduleNumber, classIndex) =>
    sortConfig.type === 'class' && sortConfig.moduleNumber === moduleNumber && sortConfig.classIndex === classIndex
      ? sortConfig.direction === 'asc'
        ? ' ▲'
        : ' ▼'
      : ' ↕';

  const infoStudents = useMemo(() => {
    const list = Array.isArray(teacherAttendanceList) ? teacherAttendanceList : [];
    return list.map((s, idx) => ({ ...s, __originIndex: idx }));
  }, [teacherAttendanceList]);

  const filteredStudents = useMemo(() => {
    const trimmedSearch = searchTerm.trim().toLowerCase();
    return infoStudents.filter((student) => {
      const name = String(student.name ?? '').toLowerCase();
      const userNumber = String(student.userNumber ?? '');
      return name.toLowerCase().includes(trimmedSearch) || userNumber.includes(trimmedSearch);
    });
  }, [searchTerm, infoStudents]);

  const attendanceFilteredStudents = useMemo(() => {
    if (attendanceFilter === null) return filteredStudents;
    return filteredStudents.filter((student) => {
      const absenceCnt = Number(student.absenceCnt ?? 0);
      return attendanceFilter === 'attendance' ? absenceCnt <= 0 : absenceCnt > 0;
    });
  }, [filteredStudents, attendanceFilter]);

  const sortedStudents = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const list = [...attendanceFilteredStudents];

    const compareStr = (a, b) =>
      String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

    const compareNum = (a, b) => Number(a ?? 0) - Number(b ?? 0);

    const getClassState = (student, moduleNumber, classIndex) => {
      const modules = student.attendanceSummaryDtoList ?? [];
      const module = modules.find((m) => m.moduleNumber === moduleNumber);
      return module?.canvasAttendanceStates?.[classIndex]?.moduleState ?? '';
    };

    const stateOrder = {
      ATTENDANCE: 0,
      LATE: 1,
      EXCUSED_ABSENCE: 2,
      ABSENCE: 3,
      '': 99,
    };

    list.sort((a, b) => {
      if (sortConfig.type === 'class') {
        const aState = getClassState(a, sortConfig.moduleNumber, sortConfig.classIndex);
        const bState = getClassState(b, sortConfig.moduleNumber, sortConfig.classIndex);

        const diff = ((stateOrder[aState] ?? 99) - (stateOrder[bState] ?? 99)) * dir;
        if (diff !== 0) return diff;

        return compareStr(a.userNumber, b.userNumber);
      }

      switch (sortConfig.key) {
        case 'name':
          return compareStr(a.name, b.name) * dir;

        case 'userNumber':
          return compareStr(a.userNumber, b.userNumber) * dir;

        case 'tutorName':
          return compareStr(a.tutorName, b.tutorName) * dir;

        case 'attendance': {
          const d1 = compareNum(a.attendanceCnt, b.attendanceCnt) * dir;
          if (d1 !== 0) return d1;
          return compareNum(a.absenceCnt, b.absenceCnt) * dir;
        }

        default:
          return 0;
      }
    });
    return list;
  }, [attendanceFilteredStudents, sortConfig]);

  useEffect(() => {
    if (teacherOnlineAttendanceSettingList) {
      setOnlineAttendanceSetting({
        useFlag: teacherOnlineAttendanceSettingList.useFlag || 'Y',
        acceptanceCriteria: teacherOnlineAttendanceSettingList.acceptanceCriteria || 'OVER_ACCEPTANCE_PROGRESS_RATE',
        moduleAcceptanceCriteria:
          teacherOnlineAttendanceSettingList.moduleAcceptanceCriteria || 'FINISH_ALL_CONTENT_VIDEO',
        acceptanceProgressRate: teacherOnlineAttendanceSettingList.acceptanceProgressRate || 0,
        totalPoint: teacherOnlineAttendanceSettingList.totalPoint || 30,
        minPoint: teacherOnlineAttendanceSettingList.minPoint || 0,
        latePenaltyPoint: teacherOnlineAttendanceSettingList.latePenaltyPoint || 0,
        absencePenaltyPoint: teacherOnlineAttendanceSettingList.missPenaltyPoint || 0, // API에서는 missPenaltyPoint로 사용
      });
    }
  }, [teacherOnlineAttendanceSettingList]);

  // TEACHER: 대시보드에서 선택한 지도교수로 대표교수 pre-select
  useEffect(() => {
    if (user.role !== 'TEACHER') return;
    if (!selectTeacherInfo.advisorNumber || !advisorTeachers.length || !mainTeachers.length) return;
    if (selectedMainTeacherNumber) return;
    const advisor = advisorTeachers.find(a => a.teacherNumber === selectTeacherInfo.advisorNumber);
    if (!advisor) return;
    const mainTeacher = filteredMainProfessors.find(main =>
      main.courseTeacherInfo.some(mainCourse =>
        advisor.courseTeacherInfo.some(ac => ac.sisCourseId === mainCourse.sisCourseId)
      )
    );
    if (!mainTeacher) return;
    setSelectedMainTeacherNumber(mainTeacher.teacherNumber);
  }, [advisorTeachers, mainTeachers]);

  // TEACHER: 대표교수 선택 시 지도교수 리스트 API 호출 (대시보드 동일 방식)
  useEffect(() => {
    if (user.role !== 'TEACHER') return;
    if (!selectedMainTeacherNumber || !teacherTermSelectBoxQueryData?.length || !mainTeachers.length) return;
    const selectedProfessor = mainTeachers.find(item => item.teacherNumber === selectedMainTeacherNumber);
    if (!selectedProfessor?.courseTeacherInfo?.length) return;
    const fetchAdvisors = async () => {
      const sisCourseId = selectedProfessor.courseTeacherInfo[0].sisCourseId;
      const matchedCourse = teacherTermSelectBoxQueryData.find(c => c.sis_course_id === sisCourseId);
      if (!matchedCourse) return;
      const parts = sisCourseId.split('-');
      setIsAdvisorLoading(true);
      const res = await getDataQuery(
        `/teach/info/advisors?courseId=${matchedCourse.course_id}&openYy=${parts[0]}&openTmGbn=${parts[1]}&sisCourseId=${sisCourseId}`
      );
      let filteredAdvisors = res?.data || [];
      if (isAdvisorProfessor && !isMainProfessor) {
        filteredAdvisors = filteredAdvisors.filter(advisor => advisor.teacherNumber === user.loginId);
      }
      setFilteredAdvisorList(filteredAdvisors);
      setSubjectState(prev => ({ ...prev, courseId: matchedCourse.course_id }));
      setIsAdvisorLoading(false);
    };
    fetchAdvisors();
  }, [selectedMainTeacherNumber, teacherTermSelectBoxQueryData, mainTeachers]);

  // ADMIN: 학과 선택 시 sisCourseIdState 설정
  useEffect(() => {
    if (user.role !== 'ADMIN' || !adminSubSelectBoxQueryData?.length) return;
    const firstItem = adminSubSelectBoxQueryData[0].account_course_dto.sis_course_id;
    const parts = firstItem.split('-');
    const semesterYear = `${parts[0]}-${parts[1]}`;
    setSisCourseIdState({
      openYy: parts[0] || null,
      openTmGbn: parts[1] || null,
      scCd: parts[2] || null,
      openDeptCd: semesterYear === '2024-10' ? parts[3] || null : parts.at(-1) || null,
    });
  }, [adminSubSelectBoxQueryData]);

  // ADMIN: 지도교수 선택 시 courseId 설정
  useEffect(() => {
    if (user.role !== 'ADMIN' || !selectTeacherInfo.advisorNumber || !adminSubSelectBoxQueryData?.length) return;
    const selectedAdvisor = filteredAdvisorList.find(a => a.teacherNumber === selectTeacherInfo.advisorNumber);
    if (!selectedAdvisor?.courseTeacherInfo?.length) return;
    const selectedMainProfessor = mainTeachers.find(item => item.teacherNumber === selectedMainTeacherNumber);
    const matchingCourseInfo = selectedMainProfessor
      ? selectedAdvisor.courseTeacherInfo.find(course =>
          selectedMainProfessor.courseTeacherInfo.some(mc => mc.sisCourseId === course.sisCourseId)
        )
      : selectedAdvisor.courseTeacherInfo[0];
    if (!matchingCourseInfo) return;
    const advisorSisCourseId = matchingCourseInfo.sisCourseId;
    const matchingCourse = adminSubSelectBoxQueryData.find(
      item => item.account_course_dto.sis_course_id === advisorSisCourseId
    );
    if (matchingCourse) {
      setSubjectState(prev => ({ ...prev, courseId: matchingCourse.account_course_dto.course_id }));
    }
  }, [selectTeacherInfo.advisorNumber, filteredAdvisorList]);

  // TEACHER: 지도교수 선택 시 courseId 설정
  useEffect(() => {
    if (user.role !== 'TEACHER' || !selectTeacherInfo.advisorNumber || !teacherTermSelectBoxQueryData?.length) return;
    const selectedAdvisor = filteredAdvisorList.find(a => a.teacherNumber === selectTeacherInfo.advisorNumber);
    if (!selectedAdvisor?.courseTeacherInfo?.length) return;
    const selectedMainProfessor = mainTeachers.find(item => item.teacherNumber === selectedMainTeacherNumber);
    const matchingCourseInfo = selectedMainProfessor
      ? selectedAdvisor.courseTeacherInfo.find(course =>
          selectedMainProfessor.courseTeacherInfo.some(mc => mc.sisCourseId === course.sisCourseId)
        )
      : selectedAdvisor.courseTeacherInfo[0];
    if (!matchingCourseInfo) return;
    const advisorSisCourseId = matchingCourseInfo.sisCourseId;
    const matchingCourse = teacherTermSelectBoxQueryData.find(
      item => item.sis_course_id === advisorSisCourseId
    );
    if (matchingCourse) {
      setSubjectState(prev => ({ ...prev, courseId: matchingCourse.course_id }));
    }
  }, [selectTeacherInfo.advisorNumber, filteredAdvisorList]);

  const handleAdminSelectTermBox = (e) => {
    setSelectTeacherInfo(prev => ({ ...prev, advisorNumber: null, advisorName: null }));
    setFilteredAdvisorList([]);
    setSelectedMainTeacherNumber('');
    const selected = semestersData?.data?.find(item => item.id === Number(e.target.value));
    const sisTermId = selected?.sis_term_id || '';
    const openYy = sisTermId.length >= 4 ? parseInt(sisTermId.substring(0, 4)) : null;
    const openTmGbn = sisTermId.length > 4 ? parseInt(sisTermId.substring(4)) : null;
    setSubjectState(prev => ({ ...prev, termId: e.target.value, termName: selected?.name || '', openYy, openTmGbn }));
  };

  const handleAdminSelectDeptBox = (e) => {
    setSelectTeacherInfo(prev => ({ ...prev, advisorNumber: null, advisorName: null }));
    setFilteredAdvisorList([]);
    setSelectedMainTeacherNumber('');
    if (!e.target.value) return;
    setAdminSelectState(prev => ({ ...prev, lectureId: e.target.value, lectureTitle: e.target.options[e.target.selectedIndex]?.text || '' }));
  };

  const handleAdminSelectMainTeacherBox = async (e) => {
    const selectedTeacherNumber = e.target.value;
    const selectedName = e.target.options[e.target.selectedIndex].text;
    setSelectTeacherInfo(prev => ({ ...prev, managerNumber: selectedTeacherNumber || null, managerName: selectedName || null, advisorNumber: null, advisorName: null }));
    setSelectedMainTeacherNumber(selectedTeacherNumber);
    setFilteredAdvisorList([]);
    setIsAdvisorLoading(false);
    if (!selectedTeacherNumber) return;
    const selectedProfessor = mainTeachers.find(item => item.teacherNumber === selectedTeacherNumber);
    if (!selectedProfessor?.courseTeacherInfo?.length) return;
    const sisCourseId = selectedProfessor.courseTeacherInfo[0].sisCourseId;
    const parts = sisCourseId.split('-');
    if (user.role === 'TEACHER') {
      // TEACHER: teacherTermSelectBoxQueryData 기반으로 지도교수 리스트 가져오기
      const matchedCourse = teacherTermSelectBoxQueryData?.find(c => c.sis_course_id === sisCourseId);
      if (!matchedCourse) return;
      setIsAdvisorLoading(true);
      const res = await getDataQuery(
        `/teach/info/advisors?courseId=${matchedCourse.course_id}&openYy=${parts[0]}&openTmGbn=${parts[1]}&sisCourseId=${sisCourseId}`
      );
      let filteredAdvisors = res?.data || [];
      if (isAdvisorProfessor && !isMainProfessor) {
        filteredAdvisors = filteredAdvisors.filter(advisor => advisor.teacherNumber === user.loginId);
      }
      setFilteredAdvisorList(filteredAdvisors);
      setSubjectState(prev => ({ ...prev, courseId: matchedCourse.course_id }));
      setIsAdvisorLoading(false);
      return;
    }
    // ADMIN: API 호출로 지도교수 리스트 가져오기
    const matchedCourse = adminSubSelectBoxQueryData?.find(item => item.account_course_dto.sis_course_id === sisCourseId);
    if (!matchedCourse) return;
    setIsAdvisorLoading(true);
    const res = await getDataQuery(
      `/teach/info/advisors?courseId=${matchedCourse.account_course_dto.course_id}&openYy=${parts[0]}&openTmGbn=${parts[1]}&sisCourseId=${sisCourseId}`
    );
    setFilteredAdvisorList(res?.data || []);
    setSubjectState(prev => ({ ...prev, courseId: matchedCourse.account_course_dto.course_id }));
    setIsAdvisorLoading(false);
  };

  const handleAdminSelectAdvisorBox = (e) => {
    if (!e.target.value) {
      setSelectTeacherInfo(prev => ({ ...prev, advisorNumber: null, advisorName: null }));
      setAttendanceListMode(true);
      return;
    }
    const selectedAdvisor = filteredAdvisorList.find(item => item.teacherNumber === e.target.value);
    if (!selectedAdvisor) return;
    setSelectTeacherInfo(prev => ({ ...prev, advisorNumber: selectedAdvisor.teacherNumber, advisorName: selectedAdvisor.teacherName }));
    setAttendanceListMode(false);
  };

  const handleNameClick = (userNumber, userLmsNumber, userName, attendanceCnt, absenceCnt) => {
    setSelectedStudent({ userNumber, userLmsNumber, userName, attendanceCnt, absenceCnt });
    setVisiblePopup(true);
  };

  const handleQrCreateClick = (courseId) => {
    setQrCreate({courseId});
    setVisibleQrCreatePopup(true);
  };

  const handleAllAttendanceListClick = () => {
    setAttendanceListMode(true);
  };

  const handleToggleAttendanceListClick = () => {
    setAttendanceListMode(prev => !prev);
  };

  const setDownloadLoading = useSetRecoilState(downloadLoadingState);
  const handleDownloadExcel = async () => {
    setDownloadLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
    const wsData = [];

    const firstStudent = teacherAttendanceList[0];
    const modules = firstStudent.attendanceSummaryDtoList || [];

    // 헤더 생성
    const headerRow1 = ['번호', '학번', '이름', '지도교수', '출석', '미출석'];
    // 2행 (번호~미출석은 병합할 거라 비워둠)
    const headerRow2 = ['', '', '', '', '', ''];

    modules.forEach((module) => {
      const cnt = module.canvasAttendanceStates?.length || 0;

      for (let i = 0; i < cnt; i++) {
        headerRow1.push(`${module.moduleNumber}주차`); // 위 줄: 주차
        headerRow2.push(`${i + 1}차시`); // 아래 줄: 차시
      }
    });

    wsData.push(headerRow1);
    wsData.push(headerRow2);

    // 데이터 생성 (정렬 상태 반영)
    sortedStudents?.forEach((student, index) => {
      const baseRow = [
        index + 1,
        student.userNumber,
        student.name,
        student.tutorName || '미배정',
        student.attendanceCnt,
        student.absenceCnt,
      ];

      const detailCells = [];

      student.attendanceSummaryDtoList.forEach((module) => {
        module.canvasAttendanceStates.forEach((summaryItem) => {
          let v;
          switch (summaryItem.moduleState) {
            case 'ATTENDANCE':
              v = '출석';
              break;
            case 'LATE':
              v = '지각';
              break;
            case 'EXCUSED_ABSENCE':
              v = '공결';
              break;
            case 'ABSENCE':
              v = '결석';
              break;
            default:
              v = '';
          }
          detailCells.push(v);
        });
      });

      wsData.push([...baseRow, ...detailCells]);
    });

    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const merges = [];

    for (let c = 0; c < 5; c++) {
      merges.push({ s: { r: 0, c }, e: { r: 1, c } });
    }
    let col = 5; // 0:번호,1:이름,2:학번,3:출석,4:미출석 → 다음부터 주차
    modules.forEach((module) => {
      const cnt = module.canvasAttendanceStates?.length || 0;
      if (cnt > 1) {
        merges.push({
          s: { r: 0, c: col },
          e: { r: 0, c: col + cnt - 1 },
        });
      }
      col += cnt;
    });

    ws['!merges'] = merges;
    applyExcelStyles(ws, 2);
    applyCenterAlignment(ws, 5);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');

    // 파일 다운로드
    const departmentName = adminSelectState?.lectureTitle || subjectState.subAccountName || '';
    const termName = subjectState.termName || '';
    writeExcelFile(wb, `${departmentName}_출석_${termName}_${moment().format('YYMMDD_HHmm')}.xlsx`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOnlineAttendanceSetting((prev) => ({
      ...prev,
      [name]: name === 'useFlag' ? value : parseInt(value, 10) || 0,
    }));
  };

  const handleAttendanceOnlineSettingSubmit = async () => {
    const attendanceSettingCreateDto = {
      useFlag: onlineAttendanceSetting.useFlag,
      acceptanceCriteria: onlineAttendanceSetting.acceptanceCriteria,
      moduleAcceptanceCriteria: onlineAttendanceSetting.moduleAcceptanceCriteria,
      acceptanceProgressRate: onlineAttendanceSetting.acceptanceProgressRate,
      totalPoint: onlineAttendanceSetting.totalPoint,
      minPoint: onlineAttendanceSetting.minPoint,
      latePenaltyPoint: onlineAttendanceSetting.latePenaltyPoint,
      absencePenaltyPoint: onlineAttendanceSetting.absencePenaltyPoint,
    };
    const formData = new FormData();

    // JSON 데이터로 변환 후 FormData에 추가
    const attendanceSettingJson = JSON.stringify(attendanceSettingCreateDto);
    const aattendanceSettingBlob = new Blob([attendanceSettingJson], { type: 'application/json' });
    formData.append('createDto', aattendanceSettingBlob);

    // 서버로 전송
    writeOnlineAttendanceSettingMutation.mutate(formData, {
      onSuccess: () => {
        alert('등록 성공');
        refetchOnlineAttendanceSettingList();
      },
      onError: (error) => {
        console.error('등록 실패:', error);
      },
    });
  };

  const writeOnlineAttendanceSettingMutation = useMutation({
    mutationFn: (formData) => postDataQuery(`/attendance-online-setting/${subjectState.courseId}`, formData),
    onSuccess: () => {},
    onError: (error) => {
      console.error('등록 실패:', error);
    },
  });

  return (
    <>
      {user.role === 'ADMIN' && (
        <div className='d-flex align-items-center p-3' style={{ gap: '1rem', flexWrap: 'wrap', borderBottom: '1px solid #dee2e6' }}>
          <Form.Select value={subjectState.termId || ''} onChange={handleAdminSelectTermBox} className='d-none'>
            <option value=''>학기 선택</option>
            {semestersData?.data?.map(item => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </Form.Select>
          {subjectState.openYy && subjectState.openTmGbn && (
            <Form.Select value={adminSelectState.lectureId || ''} onChange={handleAdminSelectDeptBox} className='d-none'>
              <option value=''>학과 선택</option>
              {adminSelectBoxQueryData?.map(item => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </Form.Select>
          )}
          {adminSelectState.lectureId && mainTeachers.length > 0 && (
            <Form.Select value={selectedMainTeacherNumber} onChange={handleAdminSelectMainTeacherBox} style={{ width: '200px' }}>
              <option value=''>대표교수 선택</option>
              {mainTeachers.map(item => (
                <option key={item.teacherNumber} value={item.teacherNumber}>{item.teacherName}</option>
              ))}
            </Form.Select>
          )}
          {selectedMainTeacherNumber && !isAdvisorLoading && (
            <Form.Select value={selectTeacherInfo.advisorNumber || ''} onChange={handleAdminSelectAdvisorBox} style={{ width: '200px' }}>
              <option value=''>지도교수 선택</option>
              {filteredAdvisorList.map(item => (
                <option key={item.teacherNumber} value={item.teacherNumber}>{item.teacherName}</option>
              ))}
            </Form.Select>
          )}
        </div>
      )}
      {user.role === 'TEACHER' && parseInt(subjectState.openYy) >= 2025 && teacherTermSelectBoxQueryData?.length > 0 && (
        <div className='d-flex align-items-center p-3' style={{ gap: '1rem', flexWrap: 'wrap', borderBottom: '1px solid #dee2e6' }}>
          <Form.Select value={selectedMainTeacherNumber} onChange={handleAdminSelectMainTeacherBox} style={{ width: '200px' }}>
            <option value=''>대표교수 선택</option>
            {filteredMainProfessors.map(item => (
              <option key={item.teacherNumber} value={item.teacherNumber}>
                {item.courseTeacherInfo?.map(c => c.courseName).join(', ')} - {item.teacherName}
              </option>
            ))}
          </Form.Select>
          {selectedMainTeacherNumber && !isAdvisorLoading && (
            <Form.Select value={selectTeacherInfo.advisorNumber || ''} onChange={handleAdminSelectAdvisorBox} style={{ width: '200px' }}>
              <option value=''>지도교수 선택</option>
              {filteredAdvisorList.map(item => (
                <option key={item.teacherNumber} value={item.teacherNumber}>{item.teacherName}</option>
              ))}
            </Form.Select>
          )}
        </div>
      )}
      {isAttendanceLoading ? (
        <div className='d-flex justify-content-center align-items-center py-5'>
          <div className='spinner-border spinner-border-sm me-2' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          데이터를 불러오는 중입니다...
        </div>
      ) : teacherAttendanceList ? (
        <>
          <div
            className='table-container'
            style={{ position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div
              id='sticky-header'
              className='p-3'
              style={{
                ...(visiblePopup
                  ? {}
                  : {
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '16px',
                    }),
              }}
            >
              <div>
                <ul className='d-flex mb-0'>
                  <li
                    className='me-3 d-flex'
                    style={{ cursor: 'pointer', opacity: attendanceFilter === 'attendance' ? 1 : 0.4 }}
                    onClick={() => setAttendanceFilter(prev => prev === 'attendance' ? null : 'attendance')}
                  >
                    <p>출석</p>
                    <img
                      src='assets/img/attendance.png'
                      alt='출석'
                      className='after-attendance-blue'
                      style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                    />
                  </li>
                  <li
                    className='me-3 d-flex'
                    style={{ cursor: 'pointer', opacity: attendanceFilter === 'absence' ? 1 : 0.4 }}
                    onClick={() => setAttendanceFilter(prev => prev === 'absence' ? null : 'absence')}
                  >
                    <p>미출석</p>
                    <img
                      src='assets/img/absence.png'
                      alt='미출석'
                      className='after-attendance-red'
                      style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                    />
                  </li>
                </ul>
              </div>
              <form className='d-flex justify-content-end'>
                <Form.Control
                  type='text'
                  placeholder='이름 / 학번 검색'
                  className='me-3'
                  style={{ width: '188px', fontSize: '16px' }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button variant='primary' onClick={handleDownloadExcel}>
                  <i className='bi bi-download'></i>
                </Button>
                <Button type='button' variant='primary' style={{marginLeft: '10px',}} onClick={() => handleQrCreateClick(subjectState.courseId)}>
                  <i className='bi bi-qr-code'></i>
                </Button>
                {sisCourseIdState.openYy !== '2024' && (
                  <>
                    <Button type='button' variant={!attendanceListMode ? 'primary' : 'outline-primary'} style={{marginLeft: '10px'}} onClick={() => setAttendanceListMode(false)}>
                      지도교수 리스트
                    </Button>
                    <Button type='button' variant={attendanceListMode ? 'primary' : 'outline-primary'} style={{marginLeft: '10px'}} onClick={() => { setAttendanceListMode(true); setSelectTeacherInfo(prev => ({ ...prev, advisorNumber: null, advisorName: null })); }}>
                      전체 리스트
                    </Button>
                  </>
                )}
              </form>
            </div>

            {/* 테이블 */}
            <div style={{ overflow: 'auto', flexGrow: '1' }}>
              <table
                style={{
                  width: 'max-content',
                  fontSize: '16px',
                  borderSpacing: '0',
                  borderCollapse: 'separate',
                }}
              >
                <thead
                  className='border border-start-0 border-end-0'
                  style={{ background: '#ebf1fa', border: '2px solid #dee2e6' }}
                >
                  <tr>
                    <td
                      rowSpan={3}
                      className='align-middle CHA_LIGHT border'
                      style={{
                        background: '#EBF1FA',
                        minWidth: '46px',
                        position: 'sticky',
                        top: '0',
                        left: '0',
                        zIndex: '10',
                      }} /*minWidth: '80px' */
                    >
                      <div className='d-flex justify-content-center align-items-center' style={{ fontSize: '14px' }}>
                        번호
                      </div>
                    </td>
                    <td
                      rowSpan={3}
                      className='align-middle CHA_LIGHT border'
                      style={{
                        minWidth: '70px',
                        position: 'sticky',
                        top: '0',
                        left: '46px',
                        zIndex: '10',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSortInfo('name')}
                    >
                      <div className='d-flex justify-content-center align-items-center' style={{ fontSize: '14px' }}>
                        이름{sortArrowInfo('name')}
                      </div>
                    </td>
                    <td
                      rowSpan={3}
                      className='align-middle CHA_LIGHT border'
                      style={{
                        minWidth: '95px',
                        position: 'sticky',
                        top: '0',
                        left: '116px',
                        zIndex: '10',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSortInfo('userNumber')}
                    >
                      <div className='d-flex justify-content-center align-items-center' style={{ fontSize: '14px' }}>
                        학번{sortArrowInfo('userNumber')}
                      </div>
                    </td>
                    <td
                      rowSpan={3}
                      className='align-middle CHA_LIGHT border'
                      style={{ minWidth: '100px', position: 'sticky', top: '0', left: '211px', zIndex: '10', background: 'white', cursor: 'pointer' }}
                      onClick={() => handleSortInfo('tutorName')}
                    >
                      <div className='d-flex justify-content-center align-items-center' style={{ fontSize: '14px' }}>
                        지도교수{sortArrowInfo('tutorName')}
                      </div>
                    </td>
                    <td
                      rowSpan={3}
                      className='align-middle CHA_LIGHT border'
                      style={{ minWidth: '130px', position: 'sticky', top: '0', left: '311px', zIndex: '10' }}
                      onClick={() => handleSortInfo('attendance')}
                    >
                      <div
                        className='d-flex justify-content-center align-items-center gap-1'
                        style={{ fontSize: '14px', cursor: 'pointer' }}
                      >
                        <div className='d-flex justify-content-center align-items-center gap-1'>
                          <span>출석</span>
                          <span>/</span>
                          <span>미출석</span>
                        </div>
                        {sortArrowInfo('attendance')}
                      </div>
                    </td>
                    {/*<td
                        rowSpan={3}
                        className="align-middle CHA_LIGHT border"
                        style={{ minWidth: '65px' }}
                      >
                        <div className="d-flex justify-content-center align-items-center">미출석</div>
                      </td>*/}
                    {/* Module 헤더 */}
                    {teacherAttendanceList &&
                      teacherAttendanceList[0]?.attendanceSummaryDtoList.map((module) => (
                        <td
                          key={module.moduleNumber}
                          className='border text-center'
                          colSpan={module.canvasAttendanceStates.length}
                          style={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            padding: '5px',
                            minWidth: 'auto',
                            position: 'sticky',
                            top: '0',
                            zIndex: '1',
                            background: '#ebf1fa',
                          }}
                        >
                          {module.moduleNumber}주차
                        </td>
                      ))}
                  </tr>
                  <tr>
                    {/* canvasAttendanceStates 길이에 따라 분할 */}
                    {teacherAttendanceList &&
                      teacherAttendanceList[0]?.attendanceSummaryDtoList.flatMap((module, moduleIndex) =>
                        module.canvasAttendanceStates.map((item, index) => (
                          <td
                            key={`moduleItem-${moduleIndex}-${index}`}
                            className='border text-center'
                            style={{
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              padding: '5px',
                              minWidth: 'auto',
                              position: 'sticky',
                              top: '28px',
                              zIndex: '1',
                              background: '#ebf1fa',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleSortClass(module.moduleNumber, index)}
                            title={`${module.moduleNumber}주차 ${index + 1}차시 상태로 정렬`}
                          >
                            <div className='d-flex justify-content-center align-items-center'>
                              <span>
                                {index + 1}차시{sortArrowClass(module.moduleNumber, index)}
                              </span>
                            </div>
                          </td>
                        ))
                      )}
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents?.map((student, index) => (
                    <tr key={index}>
                      {/* 기본 정보 */}
                      <td className='border' style={{ background: '#EBF1FA', position: 'sticky', left: '0px' }}>
                        <div className='d-flex justify-content-center'>{index + 1}</div>
                      </td>
                      <td className='border' style={{ position: 'sticky', left: '46px', background: '#EBF1FA' }}>
                        <div
                          className='d-flex justify-content-center align-items-center gap-1 Blue-500 cursor-pointer'
                          onClick={() => handleNameClick(student.userNumber, student.userLmsNumber, student.name, student.attendanceCnt, student.absenceCnt)}
                        >
                          {student.name}
                          {student.studentStatus && (
                            <span className='badge bg-warning text-dark' style={{ fontSize: '11px', fontWeight: 'normal' }}>{student.studentStatus}</span>
                          )}
                        </div>
                      </td>
                      <td className='border ' style={{ position: 'sticky', left: '116px', background: '#EBF1FA' }}>
                        <div className='d-flex justify-content-center'>{student.userNumber}</div>
                      </td>
                      <td className='border' style={{ background: '#EBF1FA', position: 'sticky', left: '211px' }}>
                        <div className='d-flex justify-content-center'>{student.tutorName || '미배정'}</div>
                      </td>
                      <td className='border' style={{ position: 'sticky', left: '311px', background: '#EBF1FA' }}>
                        <div className='d-flex justify-content-center gap-1'>
                          <span style={{ color: '#61BF5E' }}>{student.attendanceCnt}</span>
                          <span>/</span>
                          <span style={{ color: '#D92B2B' }}>{student.absenceCnt}</span>
                        </div>
                      </td>

                      {student.attendanceSummaryDtoList.flatMap((module) =>
                        module.canvasAttendanceStates.map((item, itemIndex) => (
                          <td key={`state-${module.moduleNumber}-${itemIndex}`} className='border'>
                            <div className='d-flex justify-content-center'>
                              <AttendState state={item.moduleState} />
                            </div>
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        ''
      )}
      {visiblePopup && (
        <OnlineAttendancePopup
          setVisiblePopup={setVisiblePopup}
          userNumber={selectedStudent.userNumber}
          userLmsNumber={selectedStudent.userLmsNumber}
          userName={selectedStudent.userName}
          courseId={subjectState.courseId}
          type={'attendance'}
          attendanceCnt={selectedStudent.attendanceCnt}
          absenceCnt={selectedStudent.absenceCnt}
        />
      )}
      {visibleQrCreatePopup && (
          <QrCreatePopup
            setVisibleQrCreatePopup={setVisibleQrCreatePopup}
            courseId={subjectState.courseId}
          />
        )}
    </>
  );
};
